import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  const links = [
    { name: 'Home', path: '/' },
    { 
      name: 'Services', 
      path: '/services/social-media-management',
      dropdown: [
        { name: 'Social Media Management', path: '/services/social-media-management' },
        { name: '1:1 Consulting', path: '/services/consulting' }
      ]
    },
    { 
      name: 'Free Tools', 
      path: '/pet-content-planner',
      dropdown: [
        { name: 'Pet Content Planner', path: '/pet-content-planner' },
        { name: 'Lead Funnel Score', path: '/lead-funnel-score' },
        { name: 'Ad Budget Calculator', path: '/ad-budget-calculator' },
        { name: 'Pricing Calculator', path: '/pricing-calculator' }
      ]
    },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isDropdownChildActive = (dropdown) => dropdown.some(child => location.pathname === child.path);

  return (
    <header className="bg-brand-primaryDark text-brand-white sticky top-0 z-[100] border-b border-[#3b4485]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold tracking-tight text-brand-white">Otso Co.</span>
          </Link>

          <nav className="hidden md:flex space-x-8 items-center">
            {links.map((link) => (
              <div 
                key={link.path} 
                className="relative group h-20 flex items-center"
                onMouseEnter={() => link.dropdown && setIsDropdownOpen(link.name)}
                onMouseLeave={() => link.dropdown && setIsDropdownOpen(null)}
              >
                {link.dropdown ? (
                  <>
                    <button className={`text-sm font-medium transition-colors flex items-center gap-1 ${isDropdownChildActive(link.dropdown) ? 'text-brand-blush' : 'text-gray-300 hover:text-brand-white'}`}>
                      {link.name}
                      <SafeIcon icon={FiChevronDown} className={`transition-transform duration-200 ${isDropdownOpen === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`absolute top-20 left-0 w-64 bg-brand-primaryDark border border-[#3b4485] rounded-b-xl shadow-2xl transition-all duration-200 origin-top ${isDropdownOpen === link.name ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                      <div className="py-2">
                        {link.dropdown.map((sub) => (
                          <Link 
                            key={sub.path} 
                            to={sub.path} 
                            className={`block px-6 py-3 text-sm transition-colors ${isActive(sub.path) ? 'text-brand-blush bg-[#1f2552]' : 'text-gray-300 hover:text-brand-blush hover:bg-[#1f2552]'}`}
                            onClick={() => setIsDropdownOpen(null)}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link 
                    to={link.path} 
                    className={`text-sm font-medium transition-colors ${isActive(link.path) ? 'text-brand-blush' : 'text-gray-300 hover:text-brand-white'}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <a 
              href="https://tidycal.com/digital-by-perry/schedule-a-call" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-brand-mediumPurple hover:bg-brand-mauve text-brand-white px-6 py-2 rounded font-bold transition-colors"
            >
              Book a Call
            </a>
          </nav>

          <button 
            className="md:hidden text-gray-300 hover:text-white"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-brand-primaryDark border-t border-[#3b4485]">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {links.map((link) => (
              <div key={link.path}>
                {link.dropdown ? (
                  <>
                    <div className="px-3 py-2 text-base font-medium text-gray-300 uppercase tracking-wider text-xs opacity-50">
                      {link.name}
                    </div>
                    <div className="pl-6 space-y-1 mt-1">
                      {link.dropdown.map((sub) => (
                        <Link 
                          key={sub.path} 
                          to={sub.path} 
                          className={`block px-3 py-2 text-sm rounded-md ${isActive(sub.path) ? 'text-brand-blush bg-[#1f2552]' : 'text-gray-400 hover:text-brand-blush'}`}
                          onClick={() => setIsOpen(false)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link 
                    to={link.path} 
                    className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-brand-blush bg-[#1f2552]' : 'text-gray-300 hover:text-white hover:bg-[#1f2552]'}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <a 
              href="https://tidycal.com/digital-by-perry/schedule-a-call" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full text-center mt-4 bg-brand-mediumPurple hover:bg-brand-mauve text-brand-white px-6 py-3 rounded font-bold transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Book a Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
}